import { useRouter } from 'next/router'
import { useState } from 'react'

import { TabPanel, TabPanels, Tabs } from '@wartek-id/tabs'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Shimmer } from '@wartek-id/shimmer'

import CustomTabList from '../../components/CustomTabList'
import {
  tabNamesSubmitted,
  tabNamesV3Submitted,
} from 'app/KinerjaPage/constants'
import TabHasilKerjaUtama from './TabHasilKerjaUtama'
import TabHasilKerjaTambahan from './TabHasilKerjaTambahan'
import TabPerilakuKerja from './TabPerilakuKerja'
import useToggleSdkContext from 'utils/useToogleSdk'
import TabTugasPokok from '../Perencanaan/TabTugasPokok'
import TabPraktikPembelajaran from '../Perencanaan/TabPraktikPembelajaran'
import TabPengembanganKompetensi from '../Perencanaan/TabPengembanganKompetensi'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'

import type { Kinerja } from 'types/kinerja'

interface KinerjaPerencanaanWaitingApprovalAppProps {
  skp: Kinerja
  isSKPFetched: boolean
}

enum TabIndex {
  HASIL_KERJA_UTAMA = 0,
  HASIL_KERJA_TAMBAHAN = 1,
  PERILAKU_KERJA = 2,
}

function KinerjaPerencanaanWaitingApprovalApp({
  skp,
  isSKPFetched,
}: KinerjaPerencanaanWaitingApprovalAppProps) {
  const router = useRouter()
  const toggles = useToggleSdkContext()
  const baseUrl = usePengelolaanKinerjaBaseUrl()

  const isTugasPokokEnabled =
    toggles?.['kinerja-tugas-pokok-enabled'] === true && skp?.tahun > 2024

  const tabNames = isTugasPokokEnabled ? tabNamesV3Submitted : tabNamesSubmitted

  const [activeTabIndex, setActiveTabIndex] = useState<number>(
    TabIndex.HASIL_KERJA_UTAMA
  )

  const backToHomeKinerja = () => {
    router.push(baseUrl)
  }

  return (
    <div className="py-[50px] px-[116px]">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          {/*
           * Note: we need to explicitly set font-size and line-height as workaround of styles collision
           * due to multiple version of @wartek-id/icon being used in this project.
           */}
          <Icon
            className="text-black !text-4xl !leading-9 print:hidden select-none"
            fontSize="lg"
            data-testid="icon-back"
            onClick={backToHomeKinerja}
          >
            arrow_back
          </Icon>

          <Text variant="display-md">Perencanaan Kinerja</Text>
        </div>
      </div>

      {!isSKPFetched ? (
        <Shimmer data-testid="loading-spinner-skp" />
      ) : (
        <div className="bg-white rounded-md mt-8 mb-[78px]">
          <Tabs onChange={setActiveTabIndex} selectedIndex={activeTabIndex}>
            <CustomTabList
              activeTabIndex={activeTabIndex}
              tabs={tabNames}
              disableInactiveTab
            />
            {isTugasPokokEnabled ? (
              <TabPanels>
                <TabPanel>
                  <TabTugasPokok
                    skp={skp}
                    onClickNext={() => setActiveTabIndex(1)}
                  />
                </TabPanel>
                <TabPanel>
                  <TabPraktikPembelajaran
                    skp={skp}
                    onClickNext={() => setActiveTabIndex(2)}
                  />
                </TabPanel>
                <TabPanel>
                  <TabPengembanganKompetensi
                    skp={skp}
                    onClickNext={() => setActiveTabIndex(3)}
                  />
                </TabPanel>
                <TabPanel>
                  <TabPerilakuKerja skp={skp} onClickNext={backToHomeKinerja} />
                </TabPanel>
              </TabPanels>
            ) : (
              <TabPanels>
                <TabPanel>
                  <TabHasilKerjaUtama
                    skp={skp}
                    onClickNext={() =>
                      setActiveTabIndex(TabIndex.HASIL_KERJA_TAMBAHAN)
                    }
                  />
                </TabPanel>
                <TabPanel>
                  <TabHasilKerjaTambahan
                    skp={skp}
                    onClickNext={() =>
                      setActiveTabIndex(TabIndex.PERILAKU_KERJA)
                    }
                  />
                </TabPanel>
                <TabPanel>
                  <TabPerilakuKerja skp={skp} onClickNext={backToHomeKinerja} />
                </TabPanel>
              </TabPanels>
            )}
          </Tabs>
        </div>
      )}
    </div>
  )
}

export default KinerjaPerencanaanWaitingApprovalApp
